import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';

interface StockProduct {
  id: string;
  name: string;
  category: string;
  price: number;
  thumbnail: string;
  stock: number;
  stockBreakdown?: Record<string, unknown>;
  colors?: { name: string; hex: string }[];
  sizes?: string[];
  status: string;
}

interface StockAlert {
  productId: string;
  productName: string;
  category: string;
  thumbnail: string;
  price: number;
  totalStock: number;
  alertType: 'out_of_stock' | 'critical' | 'low';
  variants: { label: string; stock: number; alertType: 'out_of_stock' | 'critical' | 'low' }[];
}

const ALERT_THRESHOLDS = {
  critical: 3,
  low: 10,
};

function getAlertType(stock: number): 'out_of_stock' | 'critical' | 'low' | null {
  if (stock === 0) return 'out_of_stock';
  if (stock <= ALERT_THRESHOLDS.critical) return 'critical';
  if (stock <= ALERT_THRESHOLDS.low) return 'low';
  return null;
}

function buildAlerts(products: StockProduct[]): StockAlert[] {
  const alerts: StockAlert[] = [];

  for (const product of products) {
    if (product.status !== 'approved') continue;

    const hasColors = (product.colors?.length ?? 0) > 0;
    const hasSizes = (product.sizes?.length ?? 0) > 0;
    const bd = product.stockBreakdown as Record<string, unknown> | undefined;

    const variants: StockAlert['variants'] = [];

    if (hasColors && hasSizes && bd) {
      for (const color of product.colors!) {
        for (const size of product.sizes!) {
          const colorMap = bd[color.name] as Record<string, number> | undefined;
          const stock = typeof colorMap?.[size] === 'number' ? colorMap[size] : 0;
          const alertType = getAlertType(stock);
          if (alertType) {
            variants.push({ label: `${color.name} / ${size}`, stock, alertType });
          }
        }
      }
    } else if (hasColors && !hasSizes && bd) {
      for (const color of product.colors!) {
        const stock = typeof bd[color.name] === 'number' ? (bd[color.name] as number) : 0;
        const alertType = getAlertType(stock);
        if (alertType) {
          variants.push({ label: color.name, stock, alertType });
        }
      }
    } else if (!hasColors && hasSizes && bd) {
      for (const size of product.sizes!) {
        const stock = typeof bd[size] === 'number' ? (bd[size] as number) : 0;
        const alertType = getAlertType(stock);
        if (alertType) {
          variants.push({ label: size, stock, alertType });
        }
      }
    } else {
      const totalAlertType = getAlertType(product.stock ?? 0);
      if (totalAlertType) {
        variants.push({ label: 'Total Stock', stock: product.stock ?? 0, alertType: totalAlertType });
      }
    }

    if (variants.length > 0) {
      const worstAlert = variants.reduce<'out_of_stock' | 'critical' | 'low'>((worst, v) => {
        const priority = { out_of_stock: 3, critical: 2, low: 1 };
        return priority[v.alertType] > priority[worst] ? v.alertType : worst;
      }, 'low');

      alerts.push({
        productId: product.id,
        productName: product.name,
        category: product.category,
        thumbnail: product.thumbnail,
        price: product.price,
        totalStock: product.stock ?? 0,
        alertType: worstAlert,
        variants,
      });
    }
  }

  // Sort: out_of_stock first, then critical, then low
  const priority = { out_of_stock: 3, critical: 2, low: 1 };
  return alerts.sort((a, b) => priority[b.alertType] - priority[a.alertType]);
}

interface StockAlertsPanelProps {
  onToast?: (msg: string, type: 'success' | 'error' | 'info') => void;
}

export default function StockAlertsPanel({ onToast }: StockAlertsPanelProps) {
  const [products, setProducts] = useState<StockProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'out_of_stock' | 'critical' | 'low'>('all');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [quickStockValues, setQuickStockValues] = useState<Record<string, string>>({});
  const [lastRefresh, setLastRefresh] = useState(new Date());

  const loadProducts = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('products')
        .select('id, name, category, price, thumbnail, stock, stock_breakdown, colors, sizes, status')
        .eq('status', 'approved')
        .order('stock', { ascending: true });

      if (error) throw error;

      const mapped: StockProduct[] = (data ?? []).map((p: Record<string, unknown>) => ({
        id: p.id as string,
        name: p.name as string,
        category: p.category as string,
        price: p.price as number,
        thumbnail: p.thumbnail as string,
        stock: (p.stock as number) ?? 0,
        stockBreakdown: p.stock_breakdown as Record<string, unknown> | undefined,
        colors: p.colors as { name: string; hex: string }[] | undefined,
        sizes: p.sizes as string[] | undefined,
        status: p.status as string,
      }));

      setProducts(mapped);
      setLastRefresh(new Date());
    } catch (e) {
      console.error('Stock alerts load error:', e);
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadProducts(); }, [loadProducts]);

  const alerts = buildAlerts(products);
  const filteredAlerts = filter === 'all' ? alerts : alerts.filter(a => a.alertType === filter);

  const counts = {
    all: alerts.length,
    out_of_stock: alerts.filter(a => a.alertType === 'out_of_stock').length,
    critical: alerts.filter(a => a.alertType === 'critical').length,
    low: alerts.filter(a => a.alertType === 'low').length,
  };

  const handleQuickRestock = async (productId: string, newStock: number) => {
    if (newStock < 0) return;
    setUpdatingId(productId);
    try {
      const currentProduct = products.find(p => p.id === productId);
      const stockBefore = currentProduct?.stock ?? 0;

      const { error } = await supabase
        .from('products')
        .update({ stock: newStock, out_of_stock: newStock === 0, updated_at: new Date().toISOString() })
        .eq('id', productId);

      if (error) throw error;

      // Log to stock history
      try {
        const { logStockChange } = await import('@/lib/stockHistory');
        await logStockChange({
          product_id: productId,
          product_name: currentProduct?.name ?? 'Unknown',
          category: currentProduct?.category ?? '',
          change_type: 'restock',
          change_amount: Math.abs(newStock - stockBefore),
          stock_before: stockBefore,
          stock_after: newStock,
          note: 'Stock Alerts panel se quick restock',
          performed_by: 'admin',
        });
      } catch { /* ignore log errors */ }

      setProducts(prev =>
        prev.map(p => p.id === productId ? { ...p, stock: newStock } : p)
      );
      onToast?.(`Stock updated to ${newStock} units!`, 'success');
      setQuickStockValues(prev => ({ ...prev, [productId]: '' }));
    } catch (e) {
      console.error('Quick restock error:', e);
      onToast?.('Stock update failed. Try again.', 'error');
    }
    setUpdatingId(null);
  };

  const alertConfig = {
    out_of_stock: {
      label: 'Out of Stock',
      icon: 'ri-close-circle-fill',
      bg: 'bg-red-50',
      border: 'border-red-200',
      badge: 'bg-red-100 text-red-700 border-red-200',
      dot: 'bg-red-500',
      iconColor: 'text-red-500',
      headerBg: 'bg-red-500',
    },
    critical: {
      label: 'Critical',
      icon: 'ri-error-warning-fill',
      bg: 'bg-orange-50',
      border: 'border-orange-200',
      badge: 'bg-orange-100 text-orange-700 border-orange-200',
      dot: 'bg-orange-500',
      iconColor: 'text-orange-500',
      headerBg: 'bg-orange-500',
    },
    low: {
      label: 'Low Stock',
      icon: 'ri-alert-fill',
      bg: 'bg-amber-50',
      border: 'border-amber-200',
      badge: 'bg-amber-100 text-amber-700 border-amber-200',
      dot: 'bg-amber-400',
      iconColor: 'text-amber-500',
      headerBg: 'bg-amber-400',
    },
  };

  return (
    <div className="space-y-4">
      {/* Header Card */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center shadow-lg shadow-red-500/25">
              <i className="ri-alarm-warning-line text-white text-xl"></i>
            </div>
            <div>
              <h2 className="text-gray-900 font-bold text-base">Stock Alerts</h2>
              <p className="text-gray-400 text-xs">
                Last updated: {lastRefresh.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
              </p>
            </div>
          </div>
          <button
            onClick={loadProducts}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gray-100 text-gray-600 text-sm font-semibold hover:bg-gray-200 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
          >
            <i className={`ri-refresh-line ${loading ? 'animate-spin' : ''}`}></i>
            Refresh
          </button>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-3 gap-3 mt-4">
          <div className="bg-red-50 border border-red-100 rounded-xl p-3 text-center">
            <p className="text-red-600 font-bold text-2xl">{counts.out_of_stock}</p>
            <p className="text-red-500 text-xs font-semibold mt-0.5">Out of Stock</p>
          </div>
          <div className="bg-orange-50 border border-orange-100 rounded-xl p-3 text-center">
            <p className="text-orange-600 font-bold text-2xl">{counts.critical}</p>
            <p className="text-orange-500 text-xs font-semibold mt-0.5">Critical (&le;3)</p>
          </div>
          <div className="bg-amber-50 border border-amber-100 rounded-xl p-3 text-center">
            <p className="text-amber-600 font-bold text-2xl">{counts.low}</p>
            <p className="text-amber-500 text-xs font-semibold mt-0.5">Low (&le;10)</p>
          </div>
        </div>

        {/* All clear state */}
        {alerts.length === 0 && !loading && (
          <div className="mt-4 flex items-center gap-3 bg-green-50 border border-green-200 rounded-xl p-4">
            <div className="w-10 h-10 rounded-xl bg-green-500 flex items-center justify-center shrink-0">
              <i className="ri-checkbox-circle-fill text-white text-lg"></i>
            </div>
            <div>
              <p className="text-green-800 font-bold text-sm">Sab theek hai!</p>
              <p className="text-green-600 text-xs">Koi bhi product low stock mein nahi hai. Sab products ka stock achha hai.</p>
            </div>
          </div>
        )}
      </div>

      {/* Filter Tabs */}
      {alerts.length > 0 && (
        <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
          {([
            { key: 'all', label: 'All Alerts', icon: 'ri-alarm-warning-line', count: counts.all, activeClass: 'bg-gray-900 text-white' },
            { key: 'out_of_stock', label: 'Out of Stock', icon: 'ri-close-circle-fill', count: counts.out_of_stock, activeClass: 'bg-red-500 text-white' },
            { key: 'critical', label: 'Critical', icon: 'ri-error-warning-fill', count: counts.critical, activeClass: 'bg-orange-500 text-white' },
            { key: 'low', label: 'Low Stock', icon: 'ri-alert-fill', count: counts.low, activeClass: 'bg-amber-400 text-white' },
          ] as const).map(tab => (
            <button
              key={tab.key}
              onClick={() => setFilter(tab.key)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold whitespace-nowrap transition-all cursor-pointer shrink-0 ${
                filter === tab.key
                  ? tab.activeClass
                  : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
              }`}
            >
              <i className={tab.icon}></i>
              {tab.label}
              {tab.count > 0 && (
                <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                  filter === tab.key ? 'bg-white/25 text-white' : 'bg-gray-100 text-gray-600'
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex flex-col items-center justify-center py-16 gap-4 bg-white rounded-2xl border border-gray-100">
          <div className="w-10 h-10 rounded-full border-4 border-gray-200 border-t-red-500 animate-spin"></div>
          <p className="text-gray-500 text-sm">Stock data load ho raha hai...</p>
        </div>
      )}

      {/* Alert Cards */}
      {!loading && filteredAlerts.length === 0 && alerts.length > 0 && (
        <div className="flex flex-col items-center justify-center py-12 gap-3 bg-white rounded-2xl border border-gray-100">
          <i className="ri-filter-3-line text-3xl text-gray-300"></i>
          <p className="text-gray-500 text-sm">Is filter mein koi alert nahi hai</p>
        </div>
      )}

      {!loading && filteredAlerts.map(alert => {
        const config = alertConfig[alert.alertType];
        const isExpanded = expandedId === alert.productId;
        const quickVal = quickStockValues[alert.productId] ?? '';

        return (
          <div
            key={alert.productId}
            className={`rounded-2xl border-2 overflow-hidden transition-all ${config.bg} ${config.border}`}
          >
            {/* Alert Card Header */}
            <div
              className="flex items-center gap-3 p-4 cursor-pointer"
              onClick={() => setExpandedId(isExpanded ? null : alert.productId)}
            >
              {/* Product Thumbnail */}
              <div className="w-14 h-14 rounded-xl overflow-hidden bg-white border border-gray-100 shrink-0 relative">
                {alert.thumbnail ? (
                  <img src={alert.thumbnail} alt={alert.productName} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <i className="ri-image-line text-gray-300 text-xl"></i>
                  </div>
                )}
                {/* Alert badge on image */}
                <div className={`absolute -top-1 -right-1 w-5 h-5 rounded-full ${config.headerBg} flex items-center justify-center`}>
                  <i className={`${config.icon} text-white text-[10px]`}></i>
                </div>
              </div>

              {/* Product Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <p className="text-gray-900 font-bold text-sm truncate">{alert.productName}</p>
                  <span className={`shrink-0 px-2 py-0.5 rounded-full text-[10px] font-bold border ${config.badge}`}>
                    {config.label}
                  </span>
                </div>
                <p className="text-gray-500 text-xs mt-0.5">{alert.category} · ₹{alert.price}</p>
                <div className="flex items-center gap-2 mt-1.5">
                  <div className="flex items-center gap-1">
                    <span className={`w-2 h-2 rounded-full ${config.dot} ${alert.alertType === 'out_of_stock' ? '' : 'animate-pulse'}`}></span>
                    <span className={`text-xs font-bold ${config.iconColor}`}>
                      {alert.totalStock === 0 ? 'Stock khatam!' : `${alert.totalStock} units bache`}
                    </span>
                  </div>
                  <span className="text-gray-300">·</span>
                  <span className="text-gray-400 text-xs">{alert.variants.length} variant{alert.variants.length > 1 ? 's' : ''} affected</span>
                </div>
              </div>

              {/* Expand arrow */}
              <div className="shrink-0">
                <i className={`text-gray-400 text-lg transition-transform ${isExpanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`}></i>
              </div>
            </div>

            {/* Expanded Details */}
            {isExpanded && (
              <div className="px-4 pb-4 border-t border-white/50 space-y-3">
                {/* Variant breakdown */}
                <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                  <div className="px-3 py-2 border-b border-gray-100">
                    <p className="text-gray-700 font-bold text-xs uppercase tracking-wide">Affected Variants</p>
                  </div>
                  <div className="divide-y divide-gray-50">
                    {alert.variants.map((v, idx) => {
                      const vConfig = alertConfig[v.alertType];
                      return (
                        <div key={idx} className="flex items-center justify-between px-3 py-2.5">
                          <div className="flex items-center gap-2">
                            <span className={`w-2 h-2 rounded-full ${vConfig.dot}`}></span>
                            <span className="text-gray-700 text-sm font-medium">{v.label}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-bold border ${vConfig.badge}`}>
                              {v.stock === 0 ? 'Out' : `${v.stock} left`}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Quick Restock */}
                <div className="bg-white rounded-xl border border-gray-100 p-3">
                  <p className="text-gray-700 font-bold text-xs mb-2 flex items-center gap-1.5">
                    <i className="ri-add-box-line text-gray-500"></i>
                    Quick Total Stock Update
                  </p>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      min="0"
                      value={quickVal}
                      onChange={e => setQuickStockValues(prev => ({ ...prev, [alert.productId]: e.target.value }))}
                      placeholder="New total stock..."
                      className="flex-1 px-3 py-2.5 rounded-xl border border-gray-200 text-sm outline-none focus:border-gray-400 focus:ring-2 focus:ring-gray-100"
                    />
                    <button
                      onClick={() => {
                        const val = parseInt(quickVal);
                        if (!isNaN(val) && val >= 0) handleQuickRestock(alert.productId, val);
                      }}
                      disabled={!quickVal || isNaN(parseInt(quickVal)) || updatingId === alert.productId}
                      className="px-4 py-2.5 rounded-xl bg-gray-900 text-white text-sm font-bold cursor-pointer disabled:opacity-40 whitespace-nowrap hover:bg-gray-800 transition-all flex items-center gap-2"
                    >
                      {updatingId === alert.productId ? (
                        <i className="ri-loader-4-line animate-spin"></i>
                      ) : (
                        <i className="ri-save-line"></i>
                      )}
                      Update
                    </button>
                  </div>
                  <p className="text-gray-400 text-xs mt-1.5">
                    Detailed variant-wise stock ke liye Products panel mein jaao
                  </p>
                </div>

                {/* Quick preset buttons */}
                <div>
                  <p className="text-gray-500 text-xs font-semibold mb-2">Quick Presets:</p>
                  <div className="flex gap-2 flex-wrap">
                    {[10, 25, 50, 100].map(preset => (
                      <button
                        key={preset}
                        onClick={() => handleQuickRestock(alert.productId, preset)}
                        disabled={updatingId === alert.productId}
                        className="px-3 py-1.5 rounded-xl bg-white border border-gray-200 text-gray-700 text-xs font-bold hover:border-gray-400 hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap disabled:opacity-40"
                      >
                        +{preset} units
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* Info Card */}
      {!loading && (
        <div className="bg-gray-50 border border-gray-200 rounded-2xl p-4">
          <p className="text-gray-700 font-bold text-sm mb-3 flex items-center gap-2">
            <i className="ri-lightbulb-flash-line text-amber-500"></i>
            Alert Thresholds
          </p>
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <span className="w-3 h-3 rounded-full bg-red-500 shrink-0"></span>
              <span className="text-gray-600 text-xs"><strong>Out of Stock:</strong> 0 units — Product customers ko nahi dikhega</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="w-3 h-3 rounded-full bg-orange-500 shrink-0"></span>
              <span className="text-gray-600 text-xs"><strong>Critical:</strong> 1-3 units — Bahut jaldi khatam hoga</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="w-3 h-3 rounded-full bg-amber-400 shrink-0"></span>
              <span className="text-gray-600 text-xs"><strong>Low Stock:</strong> 4-10 units — Restock karne ka time aa gaya</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
